﻿import { Edit, Package, Search, TrendingDown, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { InventoryItem } from "@/hooks/inventory/useInventoryData";
import {
  
  getInflationIndicator } from "@/hooks/inventory/useInventoryData";

export function InventoryList({
  filteredInventory,
  loadingInv,
  isCompact,
  searchTerm,
  setSearchTerm,
  openEditModal,
}: {
  filteredInventory: InventoryItem[];
  loadingInv: boolean;
  isCompact: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  openEditModal: (item: InventoryItem) => void;
}) {
  return (
    <div className="bg-slate-900/50 backdrop-blur-md border border-slate-800 rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-slate-800 flex flex-col sm:flex-row gap-4 justify-between items-center bg-slate-900">
        <h2 className="text-lg font-bold text-slate-200">Inventário Físico</h2>
        <div className="relative w-full sm:w-72">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
            size={16}
          />
          <Input
            placeholder="Buscar insumo ou categoria..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-slate-950 border-slate-700"
          />
        </div>
      </div>

      {!isCompact ? (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loadingInv && (
            <p className="text-slate-500 col-span-full text-center py-8">
              Carregando estoque...
            </p>
          )}

          {filteredInventory.map((item) => {
            const percent = Math.min(
              100,
              Math.max(0, (item.qty / item.max_qty) * 100),
            );
            const isLow = item.qty <= item.min_qty;
            const isMedium = item.qty <= item.min_qty * 1.5 && !isLow;
            const infl = getInflationIndicator(item);

            return (
              <div
                key={item.id}
                className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 hover:bg-slate-800 transition-colors group shadow-sm"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-bold text-slate-100">{item.name}</h3>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">
                      {item.category}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-700 hover:text-white"
                    onClick={() => openEditModal(item)}
                  >
                    <Edit size={14} />
                  </Button>
                </div>

                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-400">Nível Atual</span>
                    <span
                      className={`font-mono font-bold ${
                        isLow
                          ? "text-red-400"
                          : isMedium
                            ? "text-amber-400"
                            : "text-emerald-400"
                      }`}
                    >
                      {item.qty} {item.unit}
                    </span>
                  </div>
                  <div className="h-2 w-full bg-slate-950 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        isLow
                          ? "bg-red-500"
                          : isMedium
                            ? "bg-amber-500"
                            : "bg-emerald-500"
                      }`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs mt-1 text-slate-500">
                    <span>Mín: {item.min_qty}</span>
                    <span>Máx: {item.max_qty}</span>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-700/50 flex justify-between items-center text-sm">
                  <span className="text-slate-400">Custo Ref.</span>
                  <span className="font-medium text-slate-300 flex items-center gap-2">
                    R$ {Number(item.cost_price).toFixed(2)}
                    {infl?.type === "up" && (
                      <span className="text-red-400 text-xs flex items-center bg-red-500/10 px-1.5 py-0.5 rounded">
                        <TrendingUp size={12} className="mr-1" />
                        {infl.pct}%
                      </span>
                    )}
                    {infl?.type === "down" && (
                      <span className="text-emerald-400 text-xs flex items-center bg-emerald-500/10 px-1.5 py-0.5 rounded">
                        <TrendingDown size={12} className="mr-1" />
                        {infl.pct}%
                      </span>
                    )}
                  </span>
                </div>
              </div>
            );
          })}

          {!loadingInv && filteredInventory.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Package size={48} className="mx-auto text-slate-700 mb-3" />
              <p className="text-slate-400">Nenhum insumo encontrado.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-400 bg-slate-900/80 uppercase border-b border-slate-800">
              <tr>
                <th className="px-6 py-4">Insumo</th>
                <th className="px-6 py-4">Categoria</th>
                <th className="px-6 py-4 text-right">Qtd Atual</th>
                <th className="px-6 py-4 text-right">Custo Ref.</th>
                <th className="px-6 py-4 text-center">Status</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {loadingInv && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500">
                    Carregando estoque...
                  </td>
                </tr>
              )}
              {!loadingInv && filteredInventory.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500">
                    Nenhum insumo encontrado.
                  </td>
                </tr>
              )}
              {filteredInventory.map((item) => {
                const isLow = item.qty <= item.min_qty;
                const isMedium = item.qty <= item.min_qty * 1.5 && !isLow;
                const infl = getInflationIndicator(item);

                return (
                  <tr
                    key={item.id}
                    className="border-b border-slate-800 hover:bg-slate-800/40 transition-colors group"
                  >
                    <td className="px-6 py-3 font-bold text-slate-200">
                      {item.name}
                    </td>
                    <td className="px-6 py-3 text-slate-400 text-xs uppercase tracking-wider">
                      {item.category}
                    </td>
                    <td
                      className={`px-6 py-3 text-right font-mono font-bold ${
                        isLow
                          ? "text-red-400"
                          : isMedium
                            ? "text-amber-400"
                            : "text-emerald-400"
                      }`}
                    >
                      {item.qty} {item.unit}
                    </td>
                    <td className="px-6 py-3 text-right text-slate-300 font-mono flex items-center justify-end gap-2">
                      R$ {Number(item.cost_price).toFixed(2)}
                      {infl?.type === "up" && (
                        <span className="text-red-400 text-[10px] flex items-center">
                          <TrendingUp size={10} className="mr-0.5" />
                          {infl.pct}%
                        </span>
                      )}
                      {infl?.type === "down" && (
                        <span className="text-emerald-400 text-[10px] flex items-center">
                          <TrendingDown size={10} className="mr-0.5" />
                          {infl.pct}%
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-center">
                      <span
                        className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${
                          isLow
                            ? "bg-red-500/20 text-red-400 border border-red-500/20"
                            : isMedium
                              ? "bg-amber-500/20 text-amber-400 border border-amber-500/20"
                              : "bg-emerald-500/10 text-emerald-500 border border-emerald-500/10"
                        }`}
                      >
                        {isLow ? "Baixo" : isMedium ? "Atenção" : "Normal"}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-700 hover:text-white"
                        onClick={() => openEditModal(item)}
                      >
                        <Edit size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
